import {
  EmployerPayer,
  FeeSchedule,
  PartnerBilling,
  PartnerFeeWaiver,
  IndividualFee,
  BillingPeriodResponse,
  BillingPeriodWithEmployersResponse,
  InvestmentFeeSchedule,
  FeeCollectionModel
} from "@/shared/types";

import billingResource from "@/shared/resource/billing";
import { authorizedAxios } from "./authorizedAxios";
import { AxiosResponse } from "axios";


export function putPayer (employerId: number, args: EmployerPayer): Promise<void> {
  const data = {
    employerId,
    payerType: args.employerPays ? "employer" : "employee",
    monthlyFeeAmount: args.pricePerEmployeePerMonth,
    balanceFeeWaiverEnabled: args.balanceFeeWaiverEnabled
  };

  return authorizedAxios
    .put<any>(billingResource.employerPayer(employerId), data)
    .then(_ => null)
    .catch(_ => Promise.reject()) ;
}

export function getPayer (employerId: number): Promise<EmployerPayer> {
  return authorizedAxios.get<any>(billingResource.employerPayer(employerId))
    .then(response => ({
          employerPays: response.data.payerType === "employer",
          pricePerEmployeePerMonth: response.data.monthlyFeeAmount,
          balanceFeeWaiverEnabled: response.data.balanceFeeWaiverEnabled
        }));
}

export function getFeeSchedule (): Promise<AxiosResponse<FeeSchedule>> {
  return authorizedAxios.get<FeeSchedule>(billingResource.feeSchedule());
}

export function getPartnerBilling (partnerId: number): Promise<PartnerBilling> {
  return authorizedAxios
    .get<PartnerBilling>(billingResource.partnerBilling(partnerId))
    .then(response => response.data);
}

export function putPartnerBilling (partnerId: number, data: PartnerBilling): Promise<void> {
  return authorizedAxios
    .put(billingResource.partnerBilling(partnerId), data)
    .then(_ => null);
}

export function getIndividualFee (partnerId: number): Promise<IndividualFee> {
  return authorizedAxios
    .get<IndividualFee>(billingResource.individualFee(partnerId))
    .then(response => response.data);
}

export function putIndividualFee (partnerId: number, data: IndividualFee): Promise<void> {
  return authorizedAxios
    .put(billingResource.individualFee(partnerId), data)
    .then(_ => null);
}

export function getPartnerFeeCollection (partnerId: number) {
  return authorizedAxios.get<FeeCollectionModel>(billingResource.partnerFeeCollection(partnerId));
}
export function putPartnerFeeCollection (partnerId: number, request: FeeCollectionModel) {
  return authorizedAxios.put(billingResource.partnerFeeCollection(partnerId), request);
}
export function getEmployerFeeCollection (employerId: number) {
  return authorizedAxios.get<FeeCollectionModel>(billingResource.employerFeeCollection(employerId));
}
export function putEmployerFeeCollection (employerId: number, request: FeeCollectionModel) {
  return authorizedAxios.put(billingResource.employerFeeCollection(employerId), request);
}
export function deleteEmployerFeeCollection (employerId: number) {
  return authorizedAxios.delete(billingResource.employerFeeCollection(employerId));
}
export function getMonthlyBillingPeriods () {
  return authorizedAxios.get<BillingPeriodResponse[]>(billingResource.getMonthlyBillingPeriods());
}
export function getMonthlyBillingPeriodsByPartner (partnerId: number) {
  return authorizedAxios.get<BillingPeriodWithEmployersResponse[]>(billingResource.getMonthlyBillingPeriodsByPartner(partnerId));
}
export function getMonthlyBillingPeriodsByEmployer (employerId: number) {
  return authorizedAxios.get<BillingPeriodResponse[]>(billingResource.getMonthlyBillingPeriodsByEmployer(employerId));
}
export function getReconBillingPeriods () {
  return authorizedAxios.get<BillingPeriodWithEmployersResponse[]>(billingResource.getReconBillingPeriods());
}
export function getReconBillingPeriodsByPartner (partnerId: number) {
  return authorizedAxios.get<BillingPeriodWithEmployersResponse[]>(billingResource.getReconBillingPeriodsForPartner(partnerId));
}
export function downloadReconReport (periodId: number) {
  return authorizedAxios.get(billingResource.downloadReconReport(periodId));
}
export function downloadReport (periodId: number) {
  return authorizedAxios.get(billingResource.downloadReport(periodId));
}
export function downloadPartnerReport (
  partnerId: number,
  periodId: number,
  employerId: null | number) {
  return authorizedAxios.get(billingResource.downloadPartnerReport(
    partnerId,
    periodId,
    employerId));
}
export function downloadEmployerReport (periodId: number, employerId: number) {
  return authorizedAxios.get(billingResource.downloadEmployerReport(employerId, periodId));
}
export function downloadSummaryReport (periodId: number) {
  return authorizedAxios.get(billingResource.downloadSummaryReport(periodId));
}
export function downloadReconReportForPartner (
  partnerId: number,
  periodId: number,
  employerId: null | number) {

  return authorizedAxios.get(billingResource.downloadReconReportForPartner(
    partnerId,
    periodId,
    employerId
  ));
}
export function getPartnerFeeWaiver (partnerId: number) {
  return authorizedAxios.get<PartnerFeeWaiver>(billingResource.partnerFeeWaiver(partnerId));
}

export function updatePartnerFeeWaiver (partnerId: number, request: PartnerFeeWaiver) {
  return authorizedAxios.put(billingResource.partnerFeeWaiver(partnerId), request);
}
export function getPartnerInvestmentFeeSchedules (partnerId: number) {
  return authorizedAxios.get<InvestmentFeeSchedule[]>(billingResource.partnerInvestmentFeeSchedule(partnerId));
}
export function updatePartnerInvestmentFeeSchedules (partnerId: number, request: InvestmentFeeSchedule[]) {
  return authorizedAxios.put(billingResource.partnerInvestmentFeeSchedule(partnerId), request);
}
export function getEmployerInvestmentFeeSchedules (employerId: number) {
  return authorizedAxios.get<InvestmentFeeSchedule[]>(billingResource.employerInvestmentFeeSchedule(employerId));
}
export function updateEmployerInvestmentFeeSchedules (employerId: number, request: InvestmentFeeSchedule[]) {
  return authorizedAxios.put(billingResource.employerInvestmentFeeSchedule(employerId), request);
}
export function deleteEmployerInvestmentFeeSchedules (employerId: number) {
  return authorizedAxios.delete(billingResource.employerInvestmentFeeSchedule(employerId));
}
export function postAgreedToFeeTerms () {
  return authorizedAxios.get<void>(billingResource.agreeToFeeTerms());
}

export function getBillingHistoryYears (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<number[]>(billingResource.billingHistoryYears(), data);
}

export function getBillingHistoryMonthSummaries (year: number, username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<any[]>(billingResource.billingHistoryMonthSummaries(year), data);
}